/**
 * ПОЛНЫЙ ПУТЬ: D:\QR\QR-WEB\dashboard\src\components\Admin\AdminBusinesses\AdminAnalytics.js
 * ВЕРСИЯ: 1.0.0
 * ВРЕМЯ СОЗДАНИЯ: 15.11.2025 17:30
 * 
 * ОПИСАНИЕ: Компонент аналитики и статистики по бизнес аккаунтам
 * - Граф активности бизнесов
 * - KPI показатели
 * - Топ бизнесы по объёму
 * - Статистика по тиерам
 * - Доходность комиссий
 */

import React, { useState, useEffect } from 'react';
import './AdminAnalytics.css';

const AdminAnalytics = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState('7days');

  useEffect(() => {
    fetchAnalytics();
  }, [selectedPeriod]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('authToken');
      const response = await fetch(
        `https://qr-money.onrender.com/api/admin/businesses/analytics?period=${selectedPeriod}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setAnalytics(data.data || data || getMockAnalytics());
      console.log('✅ Аналитика загружена:', data);
    } catch (err) {
      console.error('❌ Ошибка при загрузке аналитики:', err);
      setError(err.message);
      setAnalytics(getMockAnalytics());
    } finally {
      setLoading(false);
    }
  };

  const getMockAnalytics = () => ({
    summary: {
      total_businesses: 5,
      active_businesses: 4,
      total_volume: 15000,
      total_commissions: 375,
      average_volume: 3000,
      average_commission_rate: 2.5
    },
    by_tier: [
      { tier: 1, count: 3, volume: 7000, commission_rate: 3.0 },
      { tier: 2, count: 1, volume: 5000, commission_rate: 2.5 },
      { tier: 3, count: 0, volume: 0, commission_rate: 1.5 }
    ],
    top_businesses: [
      {
        id: 1,
        company_name: 'TechCorp LLC',
        email: 'business@test.com',
        tier: 2,
        volume: 5000,
        transactions: 15,
        commission: 125,
        status: 'approved'
      },
      {
        id: 2,
        company_name: 'NewBusiness LLC',
        email: 'newbusiness@test.com',
        tier: 1,
        volume: 3500,
        transactions: 8,
        commission: 105,
        status: 'approved'
      }
    ],
    activity: [
      { date: '15.11', transactions: 8, volume: 2500 },
      { date: '14.11', transactions: 5, volume: 1800 },
      { date: '13.11', transactions: 6, volume: 2100 },
      { date: '12.11', transactions: 9, volume: 3200 },
      { date: '11.11', transactions: 7, volume: 2400 },
      { date: '10.11', transactions: 4, volume: 1500 },
      { date: '09.11', transactions: 6, volume: 2000 }
    ]
  });

  if (loading) {
    return (
      <div className="admin-analytics-page">
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <p>⏳ Загрузка аналитики...</p>
        </div>
      </div>
    );
  }

  const stats = analytics?.summary || {};
  const topBusinesses = analytics?.top_businesses || [];
  const byTier = analytics?.by_tier || [];
  const activity = analytics?.activity || [];

  return (
    <div className="admin-analytics-page">
      {/* ЗАГОЛОВОК */}
      <div className="analytics-header">
        <h1>📊 Аналитика бизнес аккаунтов</h1>
        <p>Обзор производительности и метрик</p>
      </div>

      {/* ОШИБКА */}
      {error && (
        <div className="error-message">
          ⚠️ {error} (используются тестовые данные)
        </div>
      )}

      {/* ПЕРИОД ВЫБОРА */}
      <div className="period-selector">
        <label>Период:</label>
        <select
          value={selectedPeriod}
          onChange={(e) => setSelectedPeriod(e.target.value)}
          className="period-select"
        >
          <option value="7days">📅 7 дней</option>
          <option value="30days">📅 30 дней</option>
          <option value="90days">📅 90 дней</option>
          <option value="all">📅 За все время</option>
        </select>
        <button
          className="refresh-btn"
          onClick={fetchAnalytics}
          title="Обновить"
        >
          🔄 Обновить
        </button>
      </div>

      {/* KPI СЕКЦИЯ */}
      <div className="kpi-section">
        <h2>📈 Ключевые показатели</h2>
        <div className="kpi-grid">
          <div className="kpi-card">
            <div className="kpi-icon">🏢</div>
            <div className="kpi-content">
              <p className="kpi-label">Всего бизнесов</p>
              <p className="kpi-value">{stats.total_businesses || 0}</p>
              <p className="kpi-subtitle">
                {stats.active_businesses || 0} активных
              </p>
            </div>
          </div>

          <div className="kpi-card">
            <div className="kpi-icon">💰</div>
            <div className="kpi-content">
              <p className="kpi-label">Объём (DAR)</p>
              <p className="kpi-value">
                {(stats.total_volume || 0).toLocaleString()}
              </p>
              <p className="kpi-subtitle">
                Средн: {(stats.average_volume || 0).toLocaleString()}
              </p>
            </div>
          </div>

          <div className="kpi-card">
            <div className="kpi-icon">💵</div>
            <div className="kpi-content">
              <p className="kpi-label">Комиссии (DAR)</p>
              <p className="kpi-value">{(stats.total_commissions || 0).toFixed(2)}</p>
              <p className="kpi-subtitle">
                Ср. ставка: {(stats.average_commission_rate || 0).toFixed(1)}%
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ПО ТИЕРАМ */}
      <div className="tier-section">
        <h2>🎯 Распределение по тиерам</h2>
        <div className="tier-cards">
          {byTier.map((tier, idx) => (
            <div key={idx} className="tier-card">
              <div className="tier-header">
                <h3>Tier {tier.tier}</h3>
                <span className="tier-badge">{tier.count} бизнесов</span>
              </div>
              <div className="tier-stats">
                <div className="stat-row">
                  <span>Объём:</span>
                  <strong>{tier.volume.toLocaleString()} DAR</strong>
                </div>
                <div className="stat-row">
                  <span>Комиссия:</span>
                  <strong>{tier.commission_rate}%</strong>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* АКТИВНОСТЬ */}
      <div className="activity-section">
        <h2>📈 Активность по дням</h2>
        <div className="activity-chart">
          {activity.map((day, idx) => {
            const maxVolume = Math.max(...activity.map(a => a.volume));
            const width = (day.volume / maxVolume) * 100;
            return (
              <div key={idx} className="activity-bar">
                <div className="bar-info">
                  <span className="date">{day.date}</span>
                  <span className="counts">
                    {day.transactions} транзакций • {day.volume.toLocaleString()} DAR
                  </span>
                </div>
                <div className="bar-container">
                  <div className="bar-fill" style={{ width: `${width}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ТОП БИЗНЕСЫ */}
      <div className="top-businesses-section">
        <h2>🏆 Топ бизнесы по объёму</h2>
        <div className="top-businesses-table">
          <table>
            <thead>
              <tr>
                <th>Место</th>
                <th>Компания</th>
                <th>Email</th>
                <th>Tier</th>
                <th>Объём (DAR)</th>
                <th>Транзакций</th>
                <th>Комиссии</th>
              </tr>
            </thead>
            <tbody>
              {topBusinesses.length > 0 ? (
                topBusinesses.map((business, idx) => (
                  <tr key={business.id}>
                    <td className="rank">#{idx + 1}</td>
                    <td className="company-name">{business.company_name}</td>
                    <td className="email">{business.email}</td>
                    <td className="tier">
                      <span className="tier-badge">Tier {business.tier}</span>
                    </td>
                    <td className="volume">
                      {business.volume.toLocaleString()}
                    </td>
                    <td className="count">{business.transactions}</td>
                    <td className="commission">
                      {business.commission.toFixed(2)} DAR
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" style={{ textAlign: 'center', padding: '20px' }}>
                    ❌ Нет данных
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminAnalytics;
